import cn from 'classnames';
import SEO from '$/components/SEO';
import React from 'react';
import config from '$/config';
import Loading from '$/components/Loading';
import {
  PublicTeamFragment,
  useTeamFromIdQuery,
  useTeamForDomainQuery,
} from '$/GraphQL/schema/schema';

import PublishedWebinarList from './PublishedWebinarList';

import { Redirect } from '$/utils/Redirect';
import NoWebinar from '$/components/pages/404/NoWebinar';
import { TeamHeader } from '$/components/TeamHeader';
import { useCompanySSRContext } from '$/components/pages/company';

interface Props {
  companyId?: string;
  location?: Location;
}

enum ShowCatalog {
  All = 'All',
  Subdomain = 'Subdomain',
  Company = 'Company',
  Ssr = 'Ssr',
}

const getShowMode = (loc: Location, isSSR: boolean, companyId?: string): ShowCatalog => {
  const domain: string = loc.host ?? 'app.ewebinar.com';
  if (isSSR) return ShowCatalog.Ssr;
  if (!companyId) {
    if (domain === `the-catalog.${config.DOMAIN}`) {
      return ShowCatalog.All;
    }
    return ShowCatalog.Subdomain;
  } else {
    return ShowCatalog.Company;
  }
};

const PublishedCatalogContent: React.FC<{ team?: PublicTeamFragment }> = ({ team }) => {
  return (
    <div className={`h-100vh ${cn('public-list')} min-height-100vh flex-col`}>
      {team && (
        <SEO
          title={`${team.name || 'All'} webinars`}
          image={`${team.logoMediaRef?.imageUrl || ''}`}
          url={team.shareUrl}
        />
      )}
      <TeamHeader logoMediaRef={team?.logoMediaRef} />
      <div className='public-list__body w-full flex-grow flex flex-col shadow-inner bg-blue-1 pt-6'>
        <div className='container mx-auto'>
          <PublishedWebinarList team={team} />
        </div>
      </div>
    </div>
  );
};

const CompanyPublishedCatalog: React.FC<{ companyId: string; team?: PublicTeamFragment }> = ({
  companyId,
  team: ssrTeam,
}) => {
  const teamFromIdQuery = useTeamFromIdQuery({
    variables: {
      id: companyId,
    },
    skip: config.isSSR,
  });
  const team = teamFromIdQuery.data?.team ?? ssrTeam;

  if ((!teamFromIdQuery.called || teamFromIdQuery.loading) && !ssrTeam) {
    return (
      <>
        <SEO title='eWebinar App' />
        <Loading query={teamFromIdQuery} />
      </>
    );
  }
  if (!team) {
    return <NoWebinar extensions={{ code: 'SUSPENDED' }} />;
  }
  return <PublishedCatalogContent team={team} />;
};

const SubdomainPublishedCatalog: React.FC<{ subdomain: string; team?: PublicTeamFragment }> = ({
  subdomain,
  team: ssrTeam,
}) => {
  const teamFromDomainQuery = useTeamForDomainQuery({
    variables: {
      domain: subdomain,
    },
    skip: config.isSSR,
  });
  const team = teamFromDomainQuery.data?.teamForDomain.team ?? ssrTeam;

  if (teamFromDomainQuery.called && teamFromDomainQuery.loading && !team) {
    return <Loading query={teamFromDomainQuery} />;
  }
  if (teamFromDomainQuery.data?.teamForDomain.redirectUrl) {
    document.location.href = teamFromDomainQuery.data.teamForDomain.redirectUrl;
    return null;
  }
  if (!team) {
    return <NoWebinar />;
  }
  return <PublishedCatalogContent team={team} />;
};

const PublishedCatalog: React.FC<Props> = ({ companyId, location }) => {
  const loc = location!;
  const domain: string = loc.host ?? 'app.ewebinar.com';
  const { isSSR, team } = useCompanySSRContext();
  const showMode = getShowMode(loc, isSSR, companyId);

  if (
    [`app.${config.DOMAIN}`, `home.${config.DOMAIN}`].includes(domain) &&
    showMode !== ShowCatalog.Company
  ) {
    return <Redirect to='/home' />;
  }

  switch (showMode) {
    case ShowCatalog.All:
      return <PublishedCatalogContent />;
    case ShowCatalog.Company:
      return <CompanyPublishedCatalog team={team} companyId={companyId!} />;
    case ShowCatalog.Subdomain:
      return <SubdomainPublishedCatalog team={team} subdomain={domain} />;
    case ShowCatalog.Ssr:
      if (!team) return null;
      return <CompanyPublishedCatalog team={team} companyId={team.id} />;
    default:
      return null;
  }
};

export default PublishedCatalog;
